<script setup lang="ts">
import type { IObjectValidationKeys } from '@/types/common';
import type { DynamicForm, QuestionnaireDynamicFormField } from '@/types/questionnaire';
import { useVuelidate } from '@vuelidate/core';
import { email, helpers, required } from '@vuelidate/validators';


const emit = defineEmits<{
	(e: 'onFormChange', field: QuestionnaireDynamicFormField, value: string): void
}>();

const props = defineProps({
	fields: {
		type: Array as PropType<Array<QuestionnaireDynamicFormField>>,
		required: true,
		default: () => [],
	},
	form: {
		type: Object as PropType<DynamicForm>,
		default: null,
		required: true,
	},
});

const profileStore = useProfileStore();

const dynamicForm = computed(() => props.form);
const timezone = computed(() => profileStore.profile?.timezone);

function getFieldTypeErrorMessage(fieldType: string | null) {
	switch (fieldType) {
		case FORM_TYPE.CHECKBOX:
		case FORM_TYPE.RADIO:
		case FORM_TYPE.DROPDOWN:
			return 'Please select an option.';
		case FORM_TYPE.DATE_PICKER:
			return 'Please select a date.';
		case FORM_TYPE.EMAIL:
			return 'Please enter an email.';
		case FORM_TYPE.TEL:
			return 'Please enter a phone number.';
		default:
			return 'Please provide an answer.';
	}
}

const rules = computed(() => {
	return props.fields?.reduce(
		(result: IObjectValidationKeys, field: QuestionnaireDynamicFormField) => {
			const defaultRules = {
				...(field.type === FORM_TYPE.EMAIL && { email: helpers.withMessage('Please enter a valid email address.', email) }),
				...(field.type === FORM_TYPE.TEL && { phone: helpers.withMessage('Please enter a valid phone number.', (value: string) => isValidPhoneNumber(value)) }),
			};

			if (field.isRequired) {
				const errorMessage = getFieldTypeErrorMessage(field.type);
				result[field.fieldName] = {
					required: helpers.withMessage(errorMessage, required),
					...defaultRules,
				};
			} else {
				result[field.fieldName] = {
					...defaultRules,
				};
			}
			return result;
		},
		{},
	);
});

const v$ = useVuelidate(rules, dynamicForm);


function getErrorMessage(fieldName: string) {
	const message = getValidationErrorMessage(v$.value[fieldName]?.$errors);
	return message;
}

function handleFormChange(field: QuestionnaireDynamicFormField, value: string) {
	emit('onFormChange', field, value);
}

function isFormError() {
	v$.value.$touch();
	return v$.value.$error;
}

defineExpose({
	isFormError,
});

</script>
<template>
  <div class="form-wrapper">
    <div
      v-for="(field, index) in props.fields"
      :key="index"
    >
      <div
        v-if="field.type === FORM_TYPE.TEXT"
        class="form-item form-text-field"
      >
        <BaseTextInput
          v-model="dynamicForm[field.id]"
          :label="field.label"
          :error-message="getErrorMessage(field.fieldName)"
        />
      </div>
      <div
        v-if="field.type === FORM_TYPE.TEL"
        class="form-item form-text-field"
      >
        <BaseTextInput
          v-model="dynamicForm[field.id]"
          :label="field.label"
          :error-message="getErrorMessage(field.fieldName)"
        />
      </div>
      <div
        v-if="field.type === FORM_TYPE.EMAIL"
        class="form-item form-text-field"
      >
        <BaseTextInput
          v-model="dynamicForm[field.id]"
          :label="field.label"
          type="email"
          :error-message="getErrorMessage(field.fieldName)"
        />
      </div>
      <div
        v-if="field.type === FORM_TYPE.TEXT_AREA"
        class="form-item form-text-area"
      >
        <BaseTextArea
          v-model="dynamicForm[field.id]"
          label-class="text-sm text-medium"
          rows="5"
          :label="field.label"
          :error-message="getErrorMessage(field.fieldName)"
        />
      </div>
      <div
        v-if="field.type === FORM_TYPE.RADIO"
        class="form-item form-radio"
      >
        <div class="radio-group">
          <label class="text-sm text-medium">{{ field.label }}</label>
          <p class="error-message">
            {{ getErrorMessage(field.fieldName) }}
          </p>
          <BaseRadio
            v-for="(option) in field.options"
            :id="`radio-${option.id}-${option.value}`"
            :key="`${option.id}-${option.value}`"
            v-model="dynamicForm[field.id]"
            :name="field.label"
            :label="option.label"
            :value="option.value"
          />
        </div>
      </div>
      <div
        v-if="field.type === FORM_TYPE.CHECKBOX"
        class="form-checkbox"
      >
        <div class="checkbox-group">
          <label class="text-sm text-medium">{{ field.label }}</label>
          <p class="error-message">
            {{ getErrorMessage(field.fieldName) }}
          </p>
          <BaseCheckbox
            v-for="(option) in field.options"
            :id="`checkbox-${option.id}-${option.value}`"
            :key="`${option.id}-${option.value}`"
            v-model.multipleStringWithComma="dynamicForm[field.id]"
            :name="field.label"
            :label="option.label"
            :value="option.value"
          />
        </div>
      </div>
      <div
        v-if="field.type === FORM_TYPE.DROPDOWN"
        class="form-item form-dropdown"
      >
        <label class="text-sm text-medium">{{ field.label }}</label>
        <BaseDropdown
          v-model="dynamicForm[field.id]"
          item-name="label"
          :items="field.options"
          :error-message="getErrorMessage(field.fieldName)"
        />
      </div>
      <div
        v-if="field.type === FORM_TYPE.DATE_PICKER"
        class="form-item form-datepicker"
      >
        <label class="text-sm text-medium">{{ field.label }}</label>
        <BaseDateTimePicker
          :min-date="null"
          :date="dynamicForm[field.id]"
          :date-errors="v$[field.fieldName]?.$errors"
          :timezone="timezone"
          is-date-only
          @on-date-change="(value: string)=>{
            handleFormChange(field, value)
          }"
        />
        <p
          class="error-message"
        >
          {{ getErrorMessage(field.fieldName) }}
        </p>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.form-wrapper {
    display: flex;
    flex-direction: column;
    gap: spacings-get(4);
    padding-bottom: spacings-get(6);

    .form-item {
      display: flex;
      flex-direction: column;
      gap: spacings-get(2);
    }

    .checkbox-group, .radio-group {
      display: flex;
      flex-direction: column;
      gap: spacings-get(2);

      .error-message {
        margin-top: 0;
      }
    }
}
</style>
